import { isAuthError } from "../api/client.js";

/**
 * Shared "the API call failed" banner, in two variants:
 *  - "block": nothing has loaded yet — replaces the whole screen.
 *  - "inline": some data is cached/stale — a thin strip above it.
 *
 * Distinguishes an auth expiry (401 → the fetch layer is already navigating
 * the top-level window to sign-in, see api/client.ts triggerSignInRedirect)
 * from a genuine connectivity failure (network error/5xx). The operator
 * must never see "API unreachable" for what is actually an auth expiry —
 * that read as a dead end twice (Jul-8) because there was no visible path
 * back to login. An auth expiry now always resolves within moments via the
 * automatic redirect; this banner is a brief, honest transition state.
 */
export function ConnectivityBanner({
  error,
  variant,
}: {
  error: unknown;
  variant: "block" | "inline";
}) {
  const auth = isAuthError(error);
  const message = auth
    ? "Session expired — redirecting to sign-in…"
    : variant === "block"
      ? "API unreachable — check connection and reload"
      : "Using cached data — API unreachable";

  if (variant === "block") {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: auth ? "var(--text-muted)" : "var(--danger)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        {auth ? message : `⚠ ${message}`}
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--warn-soft)",
        borderBottom: "1px solid var(--warn)",
        padding: "7px 20px",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "var(--warn)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span>{auth ? "↻" : "⚠"}</span>
      <span>{message}</span>
    </div>
  );
}
